<template>
  <div class="cart-panel col-lg-4 d-flex flex-column h-100 offcanvas-end offcanvas-lg p-0 shadow-sm bg-light"
    tabindex="-1" id="cartOffcanvas" aria-labelledby="cartOffcanvasLabel">

    <div v-if="pageLoading" class="flex-grow-1 p-3">
      <p-o-s-page-skeleton variant="bills" :show-header="false" />
    </div>

    <div v-else-if="!hasPermission" class="flex-grow-1 d-flex align-items-center justify-content-center p-3">
      <p-o-s-access-denied />
    </div>

    <template v-else>
    <div class="cart-header d-flex justify-content-between align-items-center px-3 py-3 text-white">
      <div class="d-flex align-items-center gap-2 min-w-0">
        <span class="cart-header-icon"><i class="fa-solid fa-users"></i></span>
        <div class="min-w-0">
          <div class="cart-header-eyebrow">{{ $t('Order') }}</div>
          <h5 class="mb-0 fw-bold text-truncate" id="cartOffcanvasLabel">
            {{ sessionName }}
            <span
              v-if="form.type === 'delivery' && platformInfo"
              class="badge ms-1 align-middle"
              :style="{ backgroundColor: platformInfo.color || '#6B7280' }"
            >
              {{ platformInfo.name }}<template v-if="platformInfo.external_order_ref"> · {{ platformInfo.external_order_ref }}</template>
            </span>
          </h5>
        </div>
      </div>
      <button type="button" class="btn-close btn-close-white d-lg-none" data-bs-dismiss="offcanvas"
        data-bs-target="#cartOffcanvas" aria-label="Close"></button>
      <span class="cart-header-count d-none d-lg-inline-flex align-items-center gap-1">
        <i class="fa-solid fa-basket-shopping"></i>{{ cartTotalItems }}
      </span>
    </div>

    <div ref="scrollArea" class="cart-scroll flex-grow-1 overflow-y-auto p-3 hide-scrollbar"
      style="min-height: 0; -webkit-overflow-scrolling: touch;">

      <!-- Sent Items -->
      <div class="mb-4" v-if="sentItems.length > 0">
        <h6 class="cart-section-title cart-section-title--sent">
          <i class="fa-solid fa-clock-rotate-left"></i> {{ $t('Sent') }}
          <span class="cart-section-badge">{{ sentItems.length }}</span>
        </h6>
        <div>
          <div
            class="cart-item card border-0 rounded-3 mb-2"
            :class="{ 'opacity-50': item.state === 'cancelled' }"
            v-for="item in sentItems"
            :key="'sent-' + item.id"
          >
            <div class="card-body p-2">
              <div class="d-flex justify-content-between align-items-start">
                <div class="fw-bold text-dark" style="font-size: 0.9rem;">
                  <span v-if="item.queue_no" class="badge bg-theme me-1" style="font-size: 0.7rem;">#{{ item.queue_no }}</span>
                  <span v-if="item.is_reward" class="badge bg-warning text-dark me-1" style="font-size: 0.7rem;">
                    <i class="fa-solid fa-gift me-1"></i>{{ $t('Reward') }}
                  </span>
                  {{ $ln(item, 'product_name') }}
                </div>
                <span class="fw-bold text-dark text-nowrap">
                  <template v-if="item.state === 'cancelled'">
                    <s class="text-muted">x{{ parseInt(item.quantity) }}</s>
                  </template>
                  <template v-else>
                    x{{ effectiveQty(item) }}
                    <small v-if="parseInt(item.cancelled_quantity) > 0" class="text-danger ms-1" style="font-size: 0.7rem;">
                      (<s>{{ parseInt(item.cancelled_quantity) }}</s>)
                    </small>
                  </template>
                </span>
              </div>
              <div v-if="item.modifiers && item.modifiers.length > 0" class="mt-1 ms-1">
                <template v-for="(group, gi) in groupSentModifiers(item.modifiers)" :key="'mg-'+gi">
                  <div v-if="group.name" class="text-muted fw-semibold" style="font-size: 0.72rem; line-height: 1.8;">
                    {{ $ln(group) }}
                  </div>
                  <div
                    v-for="mod in group.options"
                    :key="mod.id"
                    class="d-flex align-items-center gap-1 text-muted"
                    style="font-size: 0.78rem; line-height: 1.6;"
                  >
                    <i class="fa-solid fa-circle" style="font-size: 0.25rem;"></i>
                    <span>{{ $t(mod.name) }}</span>
                    <span v-if="parseFloat(mod.price) > 0" class="text-theme fw-semibold">+{{ $storeIndex.formatThaiCurrency(mod.price) }}</span>
                  </div>
                </template>
              </div>
              <div v-if="item.remark" class="mt-1">
                <span class="badge bg-light text-dark border" style="font-size: 0.75rem;">
                  <i class="fa-solid fa-comment-dots me-1 text-theme"></i>{{ item.remark }}
                </span>
              </div>
              <!-- item_discount promo chip: บอกชัดว่าได้ราคาพิเศษกี่ชิ้น + ลดเท่าไร -->
              <div v-if="parseInt(item.item_discount_qty) > 0 && parseFloat(item.discount) > 0" class="mt-1">
                <span class="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style="font-size: 0.72rem;">
                  <i class="fa-solid fa-tags me-1"></i>{{ $t('_promotion.Promo price') }}: {{ parseInt(item.item_discount_qty) }} {{ $t('_promotion.pcs') }}
                  (-{{ $storeIndex.formatThaiCurrency(item.discount) }})
                </span>
              </div>
              <div v-if="parseInt(item.cancelled_quantity) > 0 && item.cancelled_reason" class="mt-1">
                <span class="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style="font-size: 0.72rem;">
                  <i class="fa-solid fa-ban me-1"></i>{{ $t(item.cancelled_reason) }}
                  <span v-if="parseFloat(item.cancelled_amount) > 0" class="ms-1">
                    (-{{ $storeIndex.formatThaiCurrency(item.cancelled_amount) }})
                  </span>
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span
                  class="badge"
                  :class="stateBadgeClass(item.state)"
                >
                  <i :class="stateIcon(item.state)" class="me-1"></i>{{ $t(stateLabel(item.state)) }}
                </span>
                <span class="text-muted small">
                  <s v-if="parseInt(item.item_discount_qty) > 0 && parseFloat(item.discount) > 0" class="me-1" style="font-size: 0.72rem;">
                    {{ $storeIndex.formatThaiCurrency(item.subtotal) }}
                  </s>
                  {{ $storeIndex.formatThaiCurrency(item.total) }}
                </span>
              </div>

              <!-- Actions -->
              <div v-if="item.state !== 'cancelled'" class="mt-2">
                <!-- ของแถม: แก้/ลบ/ยกเลิกตรง ๆ ไม่ได้ ต้องยกเลิกที่แผงแต้ม (คืนแต้ม+คืนสต๊อกพร้อมกัน) -->
                <div v-if="item.is_reward" class="text-muted fst-italic d-flex align-items-center gap-1" style="font-size: 0.72rem;">
                  <i class="fa-solid fa-circle-info"></i>{{ $t('_loyalty.Cancel via Loyalty panel on check bill') }}
                </div>
                <div v-else class="d-flex gap-1">
                  <!-- POS แก้ item ได้ทุกสถานะ ยกเว้นยกเลิก (กรองด้วย v-if ชั้นนอกแล้ว) และคืนเงินแล้ว -->
                  <button
                    v-if="!item.is_refunded"
                    class="btn btn-sm btn-outline-primary border-0 px-2 py-0"
                    style="font-size: 0.78rem;"
                    @click="$emit('editSentItem', item)"
                  >
                    <i class="fa-solid fa-pen-to-square me-1"></i>{{ $t('Edit') }}
                  </button>
                  <template v-if="item.state === 'queued'">
                    <button
                      class="btn btn-sm btn-outline-danger border-0 px-2 py-0"
                      style="font-size: 0.78rem;"
                      :disabled="sentItemLoading === item.id"
                      @click="deleteSentItem(item)"
                    >
                      <i class="fa-solid fa-spinner fa-spin me-1" v-if="sentItemLoading === item.id"></i>
                      <i class="fa-solid fa-trash-can me-1" v-else></i>{{ $t('Delete') }}
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="btn btn-sm btn-outline-danger border-0 px-2 py-0"
                      style="font-size: 0.78rem;"
                      @click="cancelSentItem = item"
                    >
                      <i class="fa-solid fa-ban me-1"></i>{{ $t('Cancel') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="sentItems.length === 0" class="cart-empty mb-4">
        <span class="cart-empty-icon"><i class="fa-solid fa-clipboard-list"></i></span>
        <p class="small mb-0">{{ $t('No sent items') }}</p>
      </div>

      <hr class="cart-divider">

      <!-- New Items (Cart) -->
      <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="cart-section-title cart-section-title--new">
            <i class="fa-solid fa-plus-circle"></i> {{ $t('New Items') }}
          </h6>
          <button
            v-if="form.items.length > 0"
            class="btn btn-sm btn-link text-danger text-decoration-none p-0 small fw-semibold"
            @click="clearCart"
          >
            <i class="fa-solid fa-trash-can me-1"></i>{{ $t('Clear All') }}
          </button>
        </div>

        <div v-if="form.items.length === 0" class="cart-empty">
          <span class="cart-empty-icon cart-empty-icon--theme"><i class="fa-solid fa-cart-shopping"></i></span>
          <p class="small mb-0">{{ $t('No new items') }}</p>
        </div>

        <div v-else>
          <div
            class="cart-item cart-item--new card border-0 rounded-3 mb-2 cursor-pointer"
            :class="{ 'cart-item--unavailable': isUnavailable(item) }"
            v-for="(item, index) in form.items"
            :key="'new-' + index"
            @click="$emit('editCartItem', { index, item })"
          >
            <div class="card-body p-2">
              <div class="d-flex justify-content-between align-items-start">
                <div class="fw-bold text-dark" style="font-size: 0.9rem;">
                  {{ item.variant_name? $ln(item, 'variant_name'): $ln(item) }}
                  <!-- <span v-if="item.variant_name" class="text-muted fw-normal"> - {{ $t(item.variant_name) }}</span> -->
                </div>
                <span class="fw-bold text-dark">x{{ item.quantity }}</span>
              </div>

              <!-- ของหมด/ปิดขาย ระหว่างที่ตะกร้าค้างอยู่ — บอกรายบรรทัดว่าพังเพราะอะไร -->
              <div v-if="isUnavailable(item)" class="mt-1">
                <span
                  v-for="(issue, i) in item._unavailable"
                  :key="i"
                  class="badge bg-danger-subtle text-danger border border-danger-subtle fw-normal me-1 mb-1 text-wrap text-start"
                  style="font-size: 0.72rem;"
                >
                  <i class="fa-solid fa-circle-exclamation me-1"></i>{{ reasonLabel(issue, $t.bind(this)) }}
                </span>
              </div>
              <div v-if="item._selected_modifiers && item._selected_modifiers.length > 0" class="mt-1 ms-1">
                <template v-for="modGroup in item._selected_modifiers" :key="modGroup.modifier_id">
                  <div class="text-muted fw-semibold" style="font-size: 0.72rem; line-height: 1.8;">
                    {{ $ln(modGroup, 'modifier_name') }}
                  </div>
                  <div
                    v-for="opt in modGroup.options"
                    :key="opt.id"
                    class="d-flex align-items-center gap-1 text-muted"
                    style="font-size: 0.78rem; line-height: 1.6;"
                  >
                    <i class="fa-solid fa-circle" style="font-size: 0.25rem;"></i>
                    <span>{{ $ln(opt) }}</span>
                    <span v-if="opt.price_adjustment > 0" class="text-theme fw-semibold">+{{ $storeIndex.formatThaiCurrency(opt.price_adjustment) }}</span>
                  </div>
                </template>
              </div>
              <div v-if="item.remark" class="mt-1">
                <span class="badge bg-light text-dark border" style="font-size: 0.75rem;">
                  <i class="fa-solid fa-comment-dots me-1 text-theme"></i>{{ item.remark }}
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span class="cart-chip cart-chip--new">
                  <i class="fa-solid fa-plus-circle me-1"></i>{{ $t('New') }}
                </span>
                <span class="fw-semibold text-dark small">{{ $storeIndex.formatThaiCurrency(item._unit_price * item.quantity) }}</span>
              </div>
              <div class="mt-2 d-flex gap-1">
                <button
                  class="btn btn-sm btn-outline-primary border-0 px-2 py-0"
                  style="font-size: 0.78rem;"
                  @click.stop="$emit('editCartItem', { index, item })"
                >
                  <i class="fa-solid fa-pen-to-square me-1"></i>{{ $t('Edit') }}
                </button>
                <button
                  class="btn btn-sm btn-outline-danger border-0 px-2 py-0"
                  style="font-size: 0.78rem;"
                  @click.stop="removeItem(index)"
                >
                  <i class="fa-solid fa-trash-can me-1"></i>{{ $t('Delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div class="cart-footer p-3 z-3 mt-auto">
      <div class="cart-total-box mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <span class="text-muted small"><i class="fa-solid fa-receipt me-1"></i>{{ $t('Current Total') }}</span>
          <span class="fw-bold text-dark">{{ $storeIndex.formatThaiCurrency(sentTotal) }}</span>
        </div>
        <hr class="my-2 cart-divider">
        <div class="d-flex justify-content-between align-items-center">
          <span class="text-theme small fw-bold"><i class="fa-solid fa-plus-circle me-1"></i>{{ $t('New Items') }}</span>
          <h4 class="fw-bold text-theme mb-0">{{ $storeIndex.formatThaiCurrency(cartTotalPrice) }}</h4>
        </div>
      </div>

      <div v-for="error of v$.form.items.$errors"
        class="d-block invalid-feedback mb-2"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>

      <!-- ส่งไม่ผ่านเพราะของหมดระหว่างทาง — กู้สถานการณ์ให้จบในคลิกเดียว
           (ไม่งั้นแคชเชียร์จะกดส่งวนเจอ error เดิมโดยไม่รู้ว่าต้องเอาอะไรออก) -->
      <div v-if="hasUnavailable" class="alert alert-danger d-flex align-items-center gap-2 mb-2 py-2 px-2 rounded-3">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <div class="flex-grow-1 small text-start">{{ $t('_cart.Some items are no longer available') }}</div>
        <button
          type="button"
          class="btn btn-sm btn-danger flex-shrink-0 text-nowrap"
          :disabled="loadingSave"
          @click="handleRemoveUnavailable"
        >
          {{ $t('_cart.Remove and send') }}
        </button>
      </div>

      <div class="row g-2">
        <!--
          ออเดอร์จากแพลตฟอร์มภายนอกเก็บเงินที่เคาน์เตอร์ไม่ได้ — ลูกค้าจ่ายบนแอปไปแล้ว
          ร้านตั้งเป็นลูกหนี้แพลตฟอร์มรอโอนสิ้นเดือน (api/docs/vat-compliance.md D10)
          ฝั่ง API ปฏิเสธการรับเงินของบิลพวกนี้อยู่แล้ว ปุ่มจึงไม่ควรโผล่ให้กดตั้งแต่แรก
        -->
        <div class="col-4" v-if="!isExternalPlatform">
          <button class="btn btn-outline-success w-100 py-2 fw-bold rounded-3 h-100" @click="showCheckBill = true">
            <i class="fa-solid fa-file-invoice-dollar mb-1"></i> {{ $t('Check Bill') }}
          </button>
        </div>
        <div :class="isExternalPlatform ? 'col-12' : 'col-8'">
          <ladda
            :text="'Send to Kitchen'"
            :btn-class="'btn btn-theme w-100 py-2 fw-bold fs-6 text-white rounded-3 h-100'"
            :loading="loadingSave"
            icon="<i class='fa-solid fa-paper-plane'></i>"
            :disabled="v$.$invalid"
            :count="cartTotalItems"
            @click="handleSendToKitchen"
          />
        </div>
      </div>

      <div class="mt-2">
        <button
          class="btn btn-outline-danger w-100 py-2 fw-bold rounded-3"
          :disabled="loadingClose"
          @click="handleCloseTable"
        >
          <i class="fa-solid fa-spinner fa-spin me-1" v-if="loadingClose"></i>
          <i class="fa-solid fa-flag-checkered me-1" v-else></i>
          <!--
            ออเดอร์แพลตฟอร์มไม่มีโต๊ะให้ปิด — ปุ่มเดียวกันแต่คนละความหมาย
            ป้ายต้องสั้นระดับปุ่ม ("เสร็จสิ้น") ส่วนประโยคเต็มไปอยู่ที่หัว dialog ยืนยัน
          -->
          {{ isExternalPlatform ? $t('Finish') : $t('End Service') }}
        </button>
      </div>
    </div>
    </template>

  </div>

  <!-- Check Bill Offcanvas -->
  <offcanvas-check-bill
    v-if="showCheckBill"
    :session-name="sessionName"
    :session-token="token"
    @close="showCheckBill = false"
    @orders-changed="fetchData"
  />

  <!-- Cancel Sent Item Modal -->
  <modal-cancel-item
    v-if="cancelSentItem"
    :item="cancelSentItem"
    @close="cancelSentItem = null"
    @saved="onSentItemChanged"
  />
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import validationCart from './assets/template/validation-cart';
import { Sessions } from '@/services/endpoint/pos.service';
import { useShopStore } from '@/stores/shop.store';
import { isServiceSessionInvalid } from '@/services/endpoint/services.service';
import Ladda from '@/plugins/Ladda.vue';
import ModalCancelItem from './ModalCancelItem.vue';
import OffcanvasCheckBill from '@/components/shared/billing/OffcanvasCheckBill.vue';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import POSAccessDenied from '@/plugins/POSAccessDenied.vue';
import { usePOSSessionSocket } from '@/composables/usePOSSessionSocket';
import { useScrollRestore } from '@/composables/useScrollRestore';
import { dismissOffcanvas } from '@/utils/offcanvasHelper';
import { isItemUnavailableError, parseUnavailableItems, groupByItemIndex, reasonLabel, summaryText } from '@/utils/unavailableItems';

export default {
  name: 'CartPanel',
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validationCart(this.form);
  },
  components: {
    Ladda,
    ModalCancelItem,
    OffcanvasCheckBill,
    POSPageSkeleton,
    POSAccessDenied
  },
  data() {
    return {
      pageLoading: true,
      hasPermission: false,
      sessionName: '',
      sessionId: '',
      sentItems: [],
      sentTotal: 0,
      showCheckBill: false,
      loadingSave: false,
      loadingClose: false,
      editingRemarkIndex: null,
      sentItemLoading: null,
      cancelSentItem: null,
      // Guards the session-invalid redirect so it only navigates once.
      _redirecting: false,
      form: {
        type: 'dine_in',
        items: []
      }
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    // platform ที่เลือกตอนเปิด session delivery (เก็บใน shop store) — แนบตอนส่งครัว
    platformInfo() {
      return useShopStore().getSessionPlatform(this.token);
    },
    // แพลตฟอร์มภายนอก (Grab/LineMan) — เงินเข้าที่แอป ไม่ผ่านลิ้นชักร้าน
    isExternalPlatform() {
      return !!this.platformInfo?.is_external;
    },
    cartTotalItems() {
      return this.form.items.reduce((sum, item) => sum + item.quantity, 0);
    },
    cartTotalPrice() {
      return this.form.items.reduce((sum, item) => sum + (item._unit_price * item.quantity), 0);
    },
    // ส่งไม่ผ่านรอบก่อนเพราะมีของหมด/ปิดขาย → โชว์แถบกู้สถานการณ์เหนือปุ่มส่งครัว
    hasUnavailable() {
      return this.form.items.some(i => i._unavailable && i._unavailable.length);
    }
  },
  methods: {
    /**
     * When an API responds 422 "service session invalid!" the session this
     * page is bound to (token in the URL) no longer exists — closed by staff,
     * moved, or a stale link. Bounce back to the sessions list rather than
     * leave staff on a dead menu. Returns true when it handled the error so
     * callers can stop. Guarded so a burst of failing calls only navigates
     * once.
     */
    bounceIfSessionInvalid(err) {
      if (!isServiceSessionInvalid(err)) return false;
      if (this._redirecting) return true;
      this._redirecting = true;
      this.leaveTo({ name: 'pos.sessions' }, 'replace');
      return true;
    },
    /**
     * ทุกการ navigate ออกจากหน้านี้ต้องผ่านตรงนี้ — บนมือถือ cart คือ Bootstrap
     * offcanvas (`offcanvas-lg`) ถ้า push ทิ้งไว้ทั้งที่ยังเปิดอยู่ scroll lock
     * ของ body + backdrop จะค้างไปหน้าถัดไป (หน้า Tables เลื่อนไม่ได้)
     */
    async leaveTo(to, method = 'push') {
      await dismissOffcanvas('cartOffcanvas');
      this.$router[method](to);
    },
    async fetchData() {
      try {
        const res = await Sessions.getInfo(this.token);
        const data = res.data?.data ?? res.data;
        this.sessionName = data.name || this.displayOrderNo(data);
        this.sentTotal = parseFloat(data.sale_total) || 0;
        this.sentItems = data.items || [];
        this.form.type = data.type;
        this.hasPermission = true;
      } catch (err) {
        if (this.bounceIfSessionInvalid(err)) return;
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        } else if (err?.response?.data?.message) {
          this.$toast.warn(err.response.data.message);
        }
      } finally {
        this.pageLoading = false;
      }
    },
    displayOrderNo(session) {
      return String(session.session_no || 0).padStart(4, '0');
    },
    effectiveQty(item) {
      const qty = parseInt(item.quantity) || 0;
      const cancelled = parseInt(item.cancelled_quantity) || 0;
      return Math.max(0, qty - cancelled);
    },
    stateBadgeClass(state) {
      const map = {
        queued: 'bg-secondary',
        progressing: 'bg-warning text-dark',
        ready: 'bg-info text-dark',
        completed: 'bg-success',
        cancelled: 'bg-danger',
      };
      return map[state] || 'bg-secondary';
    },
    stateIcon(state) {
      const map = {
        queued: 'fa-solid fa-hourglass-half',
        progressing: 'fa-solid fa-fire-burner',
        ready: 'fa-solid fa-bell-concierge',
        completed: 'fa-solid fa-check-double',
        cancelled: 'fa-solid fa-ban',
      };
      return map[state] || 'fa-solid fa-circle-question';
    },
    stateLabel(state) {
      const map = {
        queued: '_POS.Queued',
        progressing: '_POS.Cooking',
        ready: '_POS.Ready',
        completed: '_POS.Completed',
        cancelled: '_POS.Cancelled',
      };
      return map[state] || state;
    },

    groupSentModifiers(modifiers) {
      if (!modifiers || modifiers.length === 0) return [];
      const hasGroups = modifiers.some(m => m.group);
      if (!hasGroups) {
        return [{ name: null, options: modifiers }];
      }
      const groups = {};
      for (const mod of modifiers) {
        const groupName = mod.group || '';
        if (!groups[groupName]) {
          groups[groupName] = { name: groupName || null, name_en: mod.group_en || null, options: [] };
        }
        groups[groupName].options.push(mod);
      }
      return Object.values(groups);
    },
    updateItem(index, newItem) {
      // แก้แถวแล้ว = ธง "สั่งไม่ได้" รอบก่อนหมดอายุ (เช่นเปลี่ยน option ที่ของหมดออก)
      this.form.items.splice(index, 1, { ...newItem, _unavailable: null });
      this.$toast.success('<b>' + (newItem.variant_name || newItem.name) + '</b> ' + this.$t('updated'));
    },

    // --- New cart item methods ---
    addItem(cartItem) {
      const existingIndex = this.form.items.findIndex(item => {
        if (item.product_id !== cartItem.product_id) return false;
        if (item._unit_price !== cartItem.unit_price) return false;
        if (item.remark || cartItem.remark) return false;
        return JSON.stringify(item.modifier_option_ids) === JSON.stringify(cartItem.modifier_option_ids);
      });

      if (existingIndex >= 0) {
        this.form.items[existingIndex].quantity += cartItem.quantity;
      } else {
        this.form.items.push({
          product_id: cartItem.product_id,
          _original_product_id: cartItem._original_product_id || cartItem.product_id,
          _variant_id: cartItem._variant_id || null,
          quantity: cartItem.quantity,
          remark: cartItem.remark || '',
          modifier_option_ids: cartItem.modifier_option_ids || [],
          _unit_price: cartItem.unit_price,
          _selected_modifiers: cartItem.selected_modifiers || [],
          name: cartItem.name,
          variant_name: cartItem.variant_name,
          variant_name_en: cartItem.variant_name_en,
        });
      }

      this.$toast.success('<b>' + (cartItem.variant_name || cartItem.name) + '</b> ' + this.$t('added to cart'));
    },
    updateItemQty(index, delta) {
      const newQty = this.form.items[index].quantity + delta;
      if (newQty <= 0) {
        this.form.items.splice(index, 1);
      } else {
        this.form.items[index].quantity = newQty;
      }
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    clearCart() {
      this.form.items = [];
      this.editingRemarkIndex = null;
    },
    async handleSendToKitchen() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.loadingSave = true;

      const payload = {
        type: this.form.type,
        items: this.form.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          remark: item.remark || null,
          modifier_option_ids: item.modifier_option_ids,
        }))
      };

      // ออเดอร์ delivery ที่เปิดพร้อม platform → BE ใช้ราคา platform + GP + ปิดบิล platform_pending
      if (this.form.type === 'delivery' && this.platformInfo) {
        payload.delivery_platform_id = this.platformInfo.delivery_platform_id;
        if (this.platformInfo.external_order_ref) {
          payload.external_order_ref = this.platformInfo.external_order_ref;
        }
      }

      // ล้างธงรอบก่อน — ผลลัพธ์รอบนี้เท่านั้นที่นับ (แคชเชียร์อาจแก้ของที่พังไปแล้ว)
      this.clearUnavailableFlags();

      await Sessions.createOrder(this.token, payload).then(() => {
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('Order sent to kitchen!'));
        this.form.items = [];
        this.v$.$reset();
        this.fetchData();
      }).catch(err => {
        if (this.bounceIfSessionInvalid(err)) return;
        // ของหมด/ปิดขาย ระหว่างที่ตะกร้าค้างอยู่ → mark แถวที่พัง + เสนอปุ่มนำออกแล้วส่งต่อ
        // (ข้อความดิบจาก server เป็น อังกฤษ + product_id ล้วน — โชว์ไปแคชเชียร์ก็แปลไม่ออก)
        if (isItemUnavailableError(err)) {
          this.flagUnavailable(err);
          return;
        }
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      }).finally(() => {
        this.loadingSave = false;
      });
    },

    /** ติดธง `_unavailable` ให้แถวที่ server ตีกลับ (item_index ตรงกับลำดับ form.items) */
    flagUnavailable(err) {
      const issues = parseUnavailableItems(err);
      const grouped = groupByItemIndex(issues);

      this.form.items = this.form.items.map((item, index) => ({
        ...item,
        _unavailable: grouped.get(index) || null,
      }));

      this.$toast.warn(summaryText(issues, this.$t.bind(this)));
    },

    clearUnavailableFlags() {
      if (!this.hasUnavailable) return;
      this.form.items = this.form.items.map(item => ({ ...item, _unavailable: null }));
    },

    /** "นำออกแล้วส่งต่อ" — ตัดแถวที่พังทิ้งแล้วยิงซ้ำให้เลย จบในคลิกเดียว */
    async handleRemoveUnavailable() {
      const removed = this.form.items.filter(i => i._unavailable && i._unavailable.length).length;
      this.form.items = this.form.items.filter(i => !(i._unavailable && i._unavailable.length));

      if (removed) {
        this.$toast.success(this.$t('_cart.Removed {n} unavailable item(s)', { n: removed }));
      }

      if (this.form.items.length) await this.handleSendToKitchen();
    },

    isUnavailable(item) {
      return !!(item._unavailable && item._unavailable.length);
    },
    // expose util ให้เทมเพลตเรียกได้ (แปล reason code → ข้อความที่พนักงานอ่านรู้เรื่อง)
    reasonLabel,

    // --- Sent item actions ---
    async deleteSentItem(item) {
      this.sentItemLoading = item.id;

      await Sessions.deleteItem(this.token, item.id).then(() => {
        this.$toast.success(this.$t('Item deleted successfully'));
        this.fetchData();
      }).catch(err => {
        if (this.bounceIfSessionInvalid(err)) return;
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      }).finally(() => {
        this.sentItemLoading = null;
      });
    },
    onSentItemChanged() {
      this.cancelSentItem = null;
      this.fetchData();
    },
    handleCloseTable() {
      // The endpoint closes the service session *and* releases the table; the
      // dialog needs to make both effects clear and warn about unpaid bills,
      // since the backend does NOT block on bills.payment_status (see API doc).
      /**
       * ออเดอร์แพลตฟอร์มกับกินที่ร้าน ปุ่มเดียวกันแต่ผลต่างกันคนละเรื่อง:
       *  - กินที่ร้าน → ปิด session + ปล่อยโต๊ะ · บิลที่ยังไม่จ่ายค้างไว้
       *  - แพลตฟอร์ม → **บันทึกเป็นยอดขายและออกใบกำกับภาษีอย่างย่อทันที**
       *    เพราะจุดความรับผิดทางภาษีเกิดตอนส่งมอบอาหาร ไม่ใช่ตอนแพลตฟอร์มโอนเงิน
       *    (api/docs/vat-compliance.md D10) เงินไปตั้งเป็นลูกหนี้แพลตฟอร์ม ไม่เข้าลิ้นชัก
       *
       * ข้อความเดิมพูดเรื่อง "ปล่อยโต๊ะ" กับ "บิลที่ยังไม่จ่ายค้างไว้" ซึ่งใช้กับแพลตฟอร์มไม่ได้เลย
       * และไม่ได้บอกผลที่สำคัญที่สุดคือมันออกเอกสารภาษีให้ทันที
       */
      const isPlatform = this.isExternalPlatform;
      const detail = isPlatform
        ? this.$t('_delivery.Close when the rider or customer has picked up the food')
        : this.$t('This will close the service session and release the table for new guests.');
      const warning = isPlatform
        ? this.$t('The sale is recorded and an abbreviated tax invoice is issued — payment is collected from the platform later.')
        : this.$t('Any unpaid bills will remain on file — settle them first if needed.');
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: isPlatform
          ? this.$t('_delivery.Finish this platform order') + '?'
          : this.$t('End service at') + ' ' + this.sessionName + '?',
        html: `<div class="text-start mb-2">${detail}</div>`
          + `<div class="small text-muted text-start"><i class="fa-solid fa-circle-info me-1"></i>${warning}</div>`,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: isPlatform ? this.$t('Finish') : this.$t('End Service'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(async res => {
        if (!res.isConfirmed) return;

        this.loadingClose = true;
        await Sessions.close(this.token).then(response => {
          const msg = response?.data?.data?.message
            || response?.data?.message
            || this.$t('Session closed');
          this.$toast.success('<b>' + this.$t('Success') + '</b> ' + msg);
          this.leaveTo({ name: 'pos.table' });
        }).catch(err => {
          if (this.bounceIfSessionInvalid(err)) return;
          if (err?.response?.data?.message) {
            this.$toast.warn(this.$t(err?.response?.data?.message));
          }
        }).finally(() => {
          this.loadingClose = false;
        });
      });
    },
    websocketUpdateState(data) {
      this.sentItems.forEach(item => {
        if (item.order_id == data.id) {
          item.state = data.state;
        }
      });
    },
    websocketItemUpdateState(data) {
      const item = this.sentItems.find(i => i.id === Number(data.id));
      if (item) {
        item.state = data.state;
      } else {
        this.fetchData();
      }
    }
  },
  mounted() {
    const token = this.$route.params.token;

    usePOSSessionSocket({
      token,
      onOrderStateUpdated: this.websocketUpdateState,
      onItemUpdated: this.websocketItemUpdateState,
    });

    this.fetchData();

    useScrollRestore(() => this.$refs.scrollArea);
  }
}
</script>

<style scoped>
/* Cart panel polish. Brand orange (#e96c15 / #d46010) matches the menu cards
   in this folder; on the POS terminal (lg+) the panel becomes a floating
   rounded card with a gap from the screen edge, while on mobile Bootstrap's
   offcanvas keeps it full-bleed. */

.min-w-0 { min-width: 0; }

@media (min-width: 992px) {
  .cart-panel {
    /* Leave a gap below so the panel/footer no longer touches the screen edge. */
    height: calc(100% - 0.85rem) !important;
    margin-bottom: 0.85rem;
    border-radius: 16px;
    overflow: hidden;                 /* clip the header/footer to the radius */
    border: 1px solid #ececec;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08),
                0 2px 8px rgba(0, 0, 0, 0.04) !important;
  }
}

/* Header — orange gradient with an eyebrow + session name + count chip. */
.cart-header {
  background: linear-gradient(135deg, #e96c15 0%, #d46010 100%);
  flex-shrink: 0;
}
.cart-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
}
.cart-header-eyebrow {
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.85;
  line-height: 1;
  margin-bottom: 2px;
}
.cart-header h5 { font-size: 1.05rem; }
.cart-header-count {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
}

/* Scroll surface. */
.cart-scroll { background: #f6f7f9; }

/* Section titles + count badge. */
.cart-section-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.6rem;
}
.cart-section-title--sent { color: #8a9099; }
.cart-section-title--new { color: #d46010; margin-bottom: 0; }
.cart-section-badge {
  background: #e4e7eb;
  color: #5b626b;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
}

/* Dashed divider between Sent and New. */
.cart-divider {
  border: none;
  border-top: 1px dashed #d7dbe0;
  opacity: 1;
}
hr.cart-divider { margin: 1rem 0; }

/* Empty placeholders — soft dashed card + tinted icon bubble. */
.cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9aa0a8;
  padding: 1.4rem 1rem;
  border: 1.5px dashed #d7dbe0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.55);
}
.cart-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #eceef1;
  color: #aeb4bc;
  font-size: 1.15rem;
  margin-bottom: 0.5rem;
}
.cart-empty-icon--theme {
  background: rgba(233, 108, 21, 0.1);
  color: #e96c15;
}

/* Item cards. */
.cart-item {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.cart-item--new {
  border-left: 3px solid #e96c15 !important;
  background: rgba(233, 108, 21, 0.04);
}
.cart-item--new:hover {
  box-shadow: 0 6px 14px rgba(233, 108, 21, 0.15);
  transform: translateY(-1px);
}
/*
  แถวที่ server ตีกลับว่าสั่งไม่ได้แล้ว — ทับแถบซ้ายสีธีมด้วยแดง ให้สแกนเจอทันที
  ว่าต้องเอาแถวไหนออก (ยังกดแก้/ลบได้ตามปกติ จึงไม่หรี่ opacity ทั้งใบ)
*/
.cart-item--unavailable {
  border-left-color: var(--bs-danger, #dc3545) !important;
  background: var(--bs-danger-bg-subtle, rgba(220, 53, 69, 0.06));
}

/* "New" chip (was blue bg-primary). */
.cart-chip--new {
  display: inline-flex;
  align-items: center;
  background: rgba(233, 108, 21, 0.12);
  color: #d46010;
  font-weight: 700;
  font-size: 0.72rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
}

/* Re-theme the (blue) outline "Edit" buttons to brand orange — scoped here,
   so it doesn't touch btn-outline-primary elsewhere. Delete/Cancel stay red. */
.btn-outline-primary {
  --bs-btn-color: #d46010;
  --bs-btn-border-color: #e9b88f;
  --bs-btn-hover-bg: #e96c15;
  --bs-btn-hover-border-color: #e96c15;
  --bs-btn-hover-color: #fff;
  --bs-btn-active-bg: #d46010;
  --bs-btn-active-border-color: #d46010;
}

/* Footer. */
.cart-footer {
  background: #fff;
  border-top: 1px solid #ececec;
  flex-shrink: 0;
}
.cart-total-box {
  background: #f6f7f9;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 0.7rem 0.9rem;
}
.cart-total-box h4 { font-size: 1.4rem; }
</style>


